<template>
  <!-- Collapsed State -->
  <div v-if="collapsed" class="camp-open-theme-zone">
    <label class="btn secondary no-dark-mode camp-toogle-theme-bt camp-toogle-open-theme-bt" @click="toggleCollapse">
      <i class="bi bi-chevron-double-right"></i>
    </label>
    <div class="tooltip1">
      <p>{{ $t("app.campaign.card.personalisation.more") }}</p>
    </div>
  </div>
  <div v-else class="camp-theme-card">
    <!-- Chevron Collapse Button -->
    <div class="camp-close-theme-bt">
      <label class="btn secondary no-dark-mode camp-toogle-theme-bt camp-toogle-close-theme-bt" @click="toggleCollapse">
        <i class="bi bi-chevron-double-left"></i>
      </label>
    </div>
    <!-- Description, Section -->
    <div class="camp-theme-title">{{ $t("app.campaign.card.personalisation.title") }}</div>
    <div class="camp-theme-text">{{ $t("app.campaign.card.personalisation.text") }}</div>
    <!-- Select campaign, Section -->
    <div class="camp-theme-title">{{ $t("app.campaign.card.personalisation.select.title") }}</div>
    <div class="camp-theme-text" v-if="allCampaigBrandings.length == 1">{{ $t("app.campaign.card.personalisation.select.noResult") }}</div>
    <div v-else>
      <div class="camp-theme-select-title">{{ $t("app.campaign.card.personalisation.theme") }}</div>
      <v-select class="camp-theme-selector" v-model="selectedCampaign" label="name" :placeholder="$t('app.campaign.card.personalisation.selectTheme')" :searchable="true" :clearable="false" :options="allCampaigBrandings" @update:model-value="selectCampaign" />
      <div class="camp-theme-text"></div>
    </div>
    <!-- File Upload Section -->
    <div class="camp-theme-title">{{ $t("app.campaign.card.personalisation.configure") }}</div>
    <div class="camp-theme-upload-title">{{ $t("app.campaign.card.personalisation.backgroundImg") }}</div>
    <div class="camp-theme-upload-selector">
      <label for="fileInput" class="camp-theme-upload-selector-bt">{{ $t("app.campaign.card.personalisation.selectFile") }}</label>
      <input type="file" id="fileInput" class="camp-theme-upload-input" accept="image/png, image/jpeg, image/gif" @change="handleFileUpload" />
      <input type="text" :value="fileName" class="camp-theme-upload-file-name" readonly />
      <img v-if="fileSelected" class="camp-theme-remove-file" src="@/assets/images/icon/remove_bg.svg" @click="removeBackground" />
    </div>
    <div class="camp-theme-upload-file-info">{{ $t("app.campaign.card.personalisation.fileInfo") }}</div>
    <!-- Colour Picker Section -->
    <div class="camp-theme-colour-section">
      <div>
        <div class="camp-theme-colour-title">{{ $t("app.campaign.card.personalisation.colors.elements") }}</div>
        <div class="camp-theme-colour-subsection">
          <input type="color" v-model="elemColour" id="bgColorPicker" class="camp-theme-colour-picker" />
          <input type="text" v-model="elemColour" class="camp-theme-colour-code" placeholder="#f2cb36" maxlength="7" />
        </div>
      </div>
      <div>
        <div class="camp-theme-colour-title">{{ $t("app.campaign.card.personalisation.colors.texts") }}</div>
        <div class="camp-theme-text-colour">
          <input type="radio" id="textWhite" value="#ffffff" v-model="textColour" />
          <label for="textWhite">{{ $t("app.campaign.card.personalisation.colors.white") }}</label>
          <input type="radio" id="textBlack" value="#000000" v-model="textColour" />
          <label for="textBlack">{{ $t("app.campaign.card.personalisation.colors.black") }}</label>
        </div>
      </div>
    </div>
    <div class="camp-theme-colour-info">{{ $t("app.campaign.card.personalisation.colors.info") }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { getImage } from "@/composables/getImage.js";
import { useCampaign, useCampaigns } from "@/queries/campaigns.query";
import { campaignsApi } from "@/api/campaigns.api";
import { useQueries } from "@tanstack/vue-query";
import { useSpaces } from "@/queries/space.query";

const route = useRoute();
const { t } = useI18n();

const { data: campaign, isPending } = useCampaign();

const yuzuElemColour = "#f2cb36"; // Default background colour
const yuzuTextColour = "#000000"; // Default text colour
const selectFileText = t("app.campaign.card.personalisation.selectFilePlaceholder"); // Default text colour

// Reactive variables
const fileName = ref(selectFileText); // Default file name
const fileSelected = ref(false); // To control the collapsed state
const elemColour = ref(yuzuElemColour);
const textColour = ref(yuzuTextColour);
const collapsed = ref(true); // To control the collapsed state
const selectedCampaign = ref(null); // Holds the selected campaign value

var lockCmpSelection = false;

if (campaign.value && !isPending.value) {
  if (campaign.value.branding.highlight_color) {
    elemColour.value = campaign.value.branding.highlight_color;
  }
  if (campaign.value.branding.text_color) {
    textColour.value = campaign.value.branding.text_color;
  }
  if (campaign.value.branding.background_image_id) {
    fileName.value = campaign.value.campaign.internal_name;
    fileSelected.value = true;
  }
}

// Emit function
const emit = defineEmits(["changeTextColour", "changeThemeColour", "toggleTheme", "newBackground", "setBackground", "setHeaderImage"]);

const toast = useToast();

const { data: spaces, isPending: isSpacesPending} = useSpaces();

const allQueries = computed(() =>  {
  if (isSpacesPending.value) return [];

  return spaces.value.map((w) => {
    return {
      queryKey: ['campaigns', { organisationId: route.params.companyId as string, spaceId: w.workspace_id }],
      queryFn: () => campaignsApi.getAll(route.params.companyId as string, w.workspace_id),
    }
  });
});

const queries = useQueries({ queries: allQueries });

const allCampaigBrandings = computed(() => {
  let index = 1;
  const list = queries.value.reduce((acc, query) => {
    if (!query?.data) return acc;
    query.data.forEach((c) => {
      index += 1;
      const listElem = {
        id: index,
        campaign_id: c.id,
        name: c.campaign.internal_name,
        elemColour: c.branding.highlight_color ? c.branding.highlight_color : yuzuElemColour,
        textColour: c.branding.text_color ? c.branding.text_color : yuzuTextColour,
        backgroundImage: c.branding.background_image_id,
        headerImage: c.branding.banner_image_id,
      };
      acc.push(listElem);
    })
    return acc;
  }, []);
  return [{
    id: 1,
    name: "Thème Yuzu par défault",
    elemColour: yuzuElemColour,
    textColour: yuzuTextColour,
    backgroundImage: null,
    headerImage: null,
  }, ...list];
})

function selectCampaign(value) {
  lockCmpSelection = true;
  // upload if no campagne or if selected campagne id is different from current
  const toUpload = !campaign.value || value.campaign_id != campaign.value.id;

  elemColour.value = value.elemColour;
  emit("changeThemeColour", value.elemColour);
  textColour.value = value.textColour;
  emit("changeTextColour", value.textColour);
  emit("setHeaderImage", { image: value.headerImage, toUpload: toUpload });
  if (value.backgroundImage) {
    const imageBGUrl = getImage(value.backgroundImage);
    emit("setBackground", { image: imageBGUrl, toUpload: toUpload }); // Emit the image URL to parent

    fileName.value = value.name;
    fileSelected.value = true;
  } else {
    fileName.value = selectFileText;
    fileSelected.value = false;
    emit("setBackground", false);
  }
  //lockCmpSelection = false;
  setTimeout(() => {
    lockCmpSelection = false;
  }, 100);
}

// Function to reset v-select value for editing
function resetSelection() {
  if (!lockCmpSelection) {
    selectedCampaign.value = null; // Reset the selection
  }
}
//In order for it to be called by parent
defineExpose({ resetSelection });

// Handle file upload and set the file name
const handleFileUpload = (event) => {
  const file = event.target.files[0];

  const maxSize = 1024 * 2048; // 2Mo

  if (file) {
    if (!["image/png", "image/jpeg", "image/gif"].includes(file.type)) {
      toast.error("Format de fichier non autorisé");
      return;
    }

    if (file.size > maxSize) {
      toast.error("Le poids de l'image dépasse 2Mo, veuillez ressayer avec une image plus légère.");
      return;
    }
    fileName.value = file.name;
    fileSelected.value = true;
    emit("newBackground", file); // Emit the image URL to parent

    resetSelection();
  }
};

function removeBackground() {
  fileName.value = selectFileText;
  fileSelected.value = false;
  emit("newBackground", false);
  resetSelection();
}

// Toggle collapse state
const toggleCollapse = () => {
  collapsed.value = !collapsed.value;
  emit("toggleTheme", collapsed.value);
};

// Watch for changes to the textColour and elemColour to emit events
watch(textColour, (newColour) => {
  emit("changeTextColour", newColour);
  resetSelection();
});

watch(elemColour, (newColour) => {
  emit("changeThemeColour", newColour);
  resetSelection();
});
</script>

<!-- <style scoped> -->
<style lang="scss" scoped>
.camp-theme-card {
  width: 650px;
  height: 580px;
  background-color: #1d1d1d;
  color: #fff;
  padding: 24px;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  border: 2px solid #464646;
}

.camp-theme-title {
  padding-top: 16px;
  font-family: "Bio Sans-Bold", Helvetica;
  font-weight: 700;
  color: #ffffff;
  font-size: 20px;
  letter-spacing: 0;
  line-height: normal;
}

.camp-theme-text {
  padding-top: 8px;
  font-family: "Bio Sans-Italic", Helvetica;
  font-style: italic;
  color: #ffffff80;
  font-size: 16px;
  letter-spacing: 0;
  line-height: normal;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--light-grey-color);
}

.camp-theme-select-title {
  padding-top: 8px;
  padding-bottom: 8px;
}

.camp-theme-selector {
  .v-select .vs__dropdown-toggle .vs__selected {
    color: #ffffff !important;
  }
}

.camp-theme-upload-title {
  padding-top: 16px;
  font-family: "Bio Sans-Regular", Helvetica;
  color: #ffffff;
  font-size: 16px;
}

.camp-theme-upload-selector {
  margin-top: 8px;
  border-radius: 4px 0px 0px 4px;
  border: 1px solid;
  border-color: #5a5a5a;
  display: flex;
  align-items: center;
}

.camp-theme-upload-selector-bt {
  padding: 12px;
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  min-width: 150px;
  font-weight: bold;
  font-family: "Bio Sans-Regular", Helvetica;
  font-weight: 400;
  color: #ffffff;
  font-size: 16px;
  border-right: 1px solid #5a5a5a;
  cursor: pointer;
  &:hover {
    background-color: #000000;
  }
}

.camp-theme-upload-input {
  display: none;
}

.camp-theme-upload-file-name {
  padding-left: 12px !important;
  border: none !important;
  color: #ffffff80 !important;
  flex-basis: 100%;
}

.camp-theme-remove-file {
  padding-right: 12px;
  cursor: pointer;
}

.camp-theme-upload-file-info {
  margin-top: 8px;
  font-size: 14px;
  font-family: "Bio Sans-Italic", Helvetica;
  font-style: italic;
  color: #ffffff80;
}

.camp-theme-colour-section {
  display: flex;
  gap: 16px;
}

.camp-theme-colour-subsection {
  display: flex;
  margin-top: 8px;
}

.camp-theme-colour-title {
  padding-top: 16px;
  font-family: "Bio Sans-Regular", Helvetica;
  color: #ffffff;
  font-size: 16px;
}

.camp-theme-colour-picker {
  border: none; /* Remove the default border */
  outline: none; /* Remove the outline when focused */
  padding: 0px !important;
  margin: 0px !important;
  width: 65px;
  height: 48px;
  cursor: pointer;
  background: none; /* Remove background to prevent any default styling */
  -webkit-appearance: none; /* Remove default webkit appearance */
}
.camp-theme-colour-picker::-webkit-color-swatch {
  border: none; /* Remove the default swatch border in WebKit browsers */
  border-radius: 4px;
}

.camp-theme-colour-picker::-moz-color-swatch {
  border: none; /* Remove the default swatch border in Firefox */
  border-radius: 4px;
}

.camp-theme-colour-code {
  width: 100px;
  background-color: #444;
  border: none;
  padding: 0px !important;
  padding-left: 12px !important;
  margin: 3.5px !important;
  font-weight: 400;
  color: #ffffff !important;
  font-size: 16px;
}

.camp-theme-text-colour {
  margin-top: 8px;
  display: flex;
  padding-top: 12px;
  gap: 16px;
}

.camp-theme-colour-info {
  margin-top: 8px;
  font-size: 14px;
  font-family: "Bio Sans-Italic", Helvetica;
  font-style: italic;
  color: #ffffff80;
}

.camp-open-theme-zone {
  position: absolute;
  display: flex;
  flex-direction: row;

  .tooltip1 {
    display: inline-block;
    position: relative;
    max-height: 36px;
    min-width: 292px;
    background-color: #1d1d1d; /* Black background with transparency */
    color: white;
    margin-left: 12px;
    padding: 10px 10px 30px 10px;
    border-radius: 8px;
    font-size: 14px;
    border: 2px solid #464646;
  }

  /* Optional arrow style */
  .tooltip1::before {
    content: "";
    position: absolute;
    left: -20px;
    bottom: 4px;
    border-width: 10px;
    border-top: 15px solid transparent;
    border-bottom: 15px solid transparent;
    border-left: 15px solid transparent;
    border-right: 15px solid #1d1d1d;
  }
}

.camp-toogle-theme-bt {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px !important;
  background-color: #1d1d1d;
  border-radius: 4px;
  border: 2px solid;
  border-color: #464646 !important;
}
</style>
